import React, { useEffect, useState } from 'react';
import {
    Box, Button, Tab, Tabs, Typography, FormControlLabel,
    Select, MenuItem, IconButton, Paper, InputBase, TextField, InputAdornment, Collapse, CircularProgress, Divider,
    AccordionSummary,
    Accordion,
    AccordionDetails,
    Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import Loader from 'components/Loader/LoaderFile';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../AlertKpi/alert-kpi.scss';
import { DashboardDetails } from './DashboardDetails';
import { WorkflowConfiguration } from './WorkflowConfiguration';
import {
    createAlertConfig,
    updateAlertConfigs,
    getAlertConfigsByProgram,
    deleteAlertConfigById,
    fetchExecutiveDashboardData,
    executiveProgramProjects,
    executiveProgramProjectsUpdate,
    activateAlertConfig
} from 'service/service';
import ExecutiveTemplateSuccessConfirmationDialog from './ExecutiveTemplateSuccessModal';
import PageWrapper from 'components/Common/PageWraper';
import { useTheme } from "@mui/material/styles";
import RichTextEditor from 'components/DataEstate/RichTextEditor';
import RemoveIcon from '@mui/icons-material/Remove';
import AlertErrorDialog from './AlertErrorDialog';
import colors from 'assets/theme/base/colors';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { AntSwitch } from './DataPipelineHelper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Option } from 'components/Dropdown/MultiFilterPanel/type';
import SearchableSelectDropdown from 'components/Dropdown/MultiFilterPanel/SearchableSelectDropdown';

// ====================================================================================
// START: Helper components
// ====================================================================================

// --- Custom Styles for Modal Inputs ---
const modalInputStyles = {
    input: { color: 'white' },
    label: { color: 'grey' },
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#1E2A45',
        '& fieldset': { borderColor: 'grey' },
        '&:hover fieldset': { borderColor: 'white' },
        '&.Mui-focused fieldset': { borderColor: '#009BD8' },
    },
    '& .MuiSvgIcon-root': { color: 'white' },
    '& .MuiAutocomplete-inputRoot': {
        padding: '3px 9px !important',
        backgroundColor: '#1E2A45',
        '& fieldset': { borderColor: 'grey' },
        '&:hover fieldset': { borderColor: 'white' },
        '&.Mui-focused fieldset': { borderColor: '#009BD8' },
    },
    '& .MuiAutocomplete-input': {
        color: 'white',
    },
};

// --- Custom Styles for White Modal Inputs ---
const whiteModalInputStyles = {
    input: { color: 'black !important', fontWeight: 'normal' },
    label: { color: 'black', fontWeight: 'normal' },
    '& .MuiInputLabel-root': { 
        color: 'black !important', 
        fontWeight: 'normal',
        '&.Mui-focused': { color: '#1976d2 !important', fontWeight: 'normal' },
        '&.MuiInputLabel-shrink': { color: 'black !important', fontWeight: 'normal' },
        '&.MuiInputLabel-outlined': { color: 'black !important', fontWeight: 'normal' }
    },
    '& .MuiFormLabel-root': {
        color: 'black !important',
        fontWeight: 'normal',
        '&.Mui-focused': { color: '#1976d2 !important' }
    },
    '& .MuiOutlinedInput-input': {
        color: 'black !important',
        fontWeight: 'normal',
        '&::placeholder': {
            color: '#666666',
            fontWeight: 'normal',
            opacity: 1
        }
    },
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        fontWeight: 'normal',
        color: 'black !important',
        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87)' },
        '&.Mui-focused fieldset': { borderColor: '#1976d2' },
        '& input': {
            color: 'black !important',
            fontWeight: 'normal',
            '-webkit-text-fill-color': 'black !important'
        },
            '& .MuiInputBase-input': {
            color: 'black !important',
            fontWeight: 'normal',
            '-webkit-text-fill-color': 'black !important'
        },
        '& .MuiSelect-select': {
            color: 'black !important',
            fontWeight: 'normal',
            '-webkit-text-fill-color': 'black !important'
        }
    },
    '& .MuiSvgIcon-root': { color: 'rgba(0, 0, 0, 0.54)' },
    '& .MuiAutocomplete-inputRoot': {
        padding: '3px 9px !important',
        backgroundColor: 'white',
        fontWeight: 'normal',
        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87)' },
        '&.Mui-focused fieldset': { borderColor: '#1976d2' },
        '& .MuiOutlinedInput-input::placeholder': {
            color: 'rgba(0, 0, 0, 0.6) !important',
            fontWeight: 'normal',
            opacity: 1
        },
        '& .MuiInputBase-input': {
            color: 'black !important',
            fontWeight: 'normal',
            '-webkit-text-fill-color': 'black !important'
        },
        '& input::placeholder': {
            color: 'rgba(0, 0, 0, 0.6) !important',
            fontWeight: 'normal',
            opacity: 1
        },
        '& .MuiAutocomplete-input::placeholder': {
            color: 'rgba(0, 0, 0, 0.6) !important',
            fontWeight: 'normal',
            opacity: 1
        }
    },
    '& .MuiAutocomplete-input': {
        color: 'black !important',
        fontWeight: 'normal',
        '-webkit-text-fill-color': 'black !important'
    },
    '& .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': {
        color: 'black !important',
        fontWeight: 'normal',
        '-webkit-text-fill-color': 'black !important',
        '&::placeholder': {
            color: 'rgba(0, 0, 0, 0.6) !important',
            '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.6) !important',
            fontWeight: 'normal',
            opacity: 1
        }
    },
    '& .MuiAutocomplete-root .MuiTextField-root .MuiInputBase-input': {
        color: 'black !important',
        fontWeight: 'normal',
        '-webkit-text-fill-color': 'black !important',
        '&::placeholder': {
            color: 'rgba(0, 0, 0, 0.6) !important',
            '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.6) !important',
            fontWeight: 'normal',
            opacity: 1
        }
    },
    '& .MuiAutocomplete-root .MuiInputBase-input::placeholder': {
        color: 'rgba(0, 0, 0, 0.6) !important',
        fontWeight: 'normal',
        opacity: 1
    },
    '& .MuiTextField-root .MuiInputBase-input::placeholder': {
        color: 'rgba(0, 0, 0, 0.6) !important',
        fontWeight: 'normal',
        opacity: 1
    },
    '& input[placeholder]': {
        color: 'black !important',
        '&::placeholder': {
            color: 'rgba(0, 0, 0, 0.6) !important',
            opacity: 1
        }
    },
    '& .MuiSelect-select': {
        color: 'black !important',
        fontWeight: 'normal',
        '-webkit-text-fill-color': 'black !important'
    },
    '& .MuiSelect-select em': {
        color: 'rgba(0, 0, 0, 0.6) !important',
        fontWeight: 'normal',
        fontStyle: 'normal'
    },
    '& .MuiMenuItem-root': {
        color: 'black !important',
        backgroundColor: 'white !important',
        fontWeight: 'normal',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
        },
        '& em': {
            color: '#666666',
            fontWeight: 'normal',
            fontStyle: 'normal'
        }
    },
    '& .MuiPaper-root': {
        backgroundColor: 'white !important',
        color: 'black !important'
    },
    '& .MuiMenu-paper': {
        backgroundColor: 'white !important',
        color: 'black !important'
    },
    '& .MuiAutocomplete-popupIndicator': {
        color: 'rgba(0, 0, 0, 0.54)',
    },
    '& .MuiAutocomplete-clearIndicator': {
        color: 'rgba(0, 0, 0, 0.54)',
    },
    '& .MuiAutocomplete-tag': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
        color: 'black',
        '& .MuiChip-deleteIcon': {
            color: 'rgba(0, 0, 0, 0.54)',
        },
    },
    '& .MuiTypography-root': {
        color: 'black !important',
        fontWeight: 'normal'
    },
    '& .MuiFormControlLabel-root .MuiTypography-root': {
        color: 'black !important',
        fontWeight: 'normal'
    },
};

// Constants for multi-select
const ALL_HOPS_VALUE = "__ALL_HOPS__";
const ALL_HOPS_LABEL = "All Hops";

// Icons for multi-select checkboxes - custom black icons
const checkBoxIcon = (
    <Box
        sx={{
            width: 20,
            height: 20,
            border: '2px solid black',
            borderRadius: '2px',
            backgroundColor: 'transparent'
        }}
    />
);
const checkedBoxIcon = (
    <Box
        sx={{
            width: 20,
            height: 20,
            border: '2px solid black',
            borderRadius: '2px',
            backgroundColor: 'black',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: '6px',
                top: '2px',
                width: '5px',
                height: '10px',
                border: 'solid white',
                borderWidth: '0 2px 2px 0',
                transform: 'rotate(45deg)'
            }
        }}
    />
);

// --- SLAs Component (REVISED) ---
interface SLAsProps {
    onValidityChange: (isValid: boolean) => void;
    onDataChange: (data: any) => void;
}
interface HopData {
    hopName: string; scheduleFrequency: string; days: string; startTime: string; criteria: string; endTime: string; duration: string; isEditing: boolean;
}
interface FlowSlaData {
    frequency: string;
    days: string;
    criteria: string;
    target: string;
}

const StyledInput: React.FC<{ value: string; onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void; endAdornment?: React.ReactNode }> = ({ value, onChange, endAdornment }) => (
    <Paper component="form" sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: 'auto', backgroundColor: '#1E2A45' }}>
        <InputBase sx={{ ml: 1, flex: 1, color: 'white' }} value={value} onChange={onChange} endAdornment={endAdornment} />
    </Paper>
);

const AutocompletePaperComponent = (props: any) => {
  const { children, ...prop } = props;

  return (
    <Paper
      {...prop}
      sx={{
        backgroundColor: 'white !important', 
        color: 'black !important',
        '& .MuiAutocomplete-option': {
            color: 'black !important',
            backgroundColor: 'white !important',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
            },
            '&[aria-selected="true"]': {
                backgroundColor: 'rgba(0, 0, 0, 0.08) !important'
            }
        }
      }}
    >
      {children}
    </Paper>
  );
};

const SCHEDULE_FREQUENCY_OPTIONS = ['Daily', 'Weekly'];

const DAYS_OPTIONS = ['All Days', 'Thu, Fri'];

const SLA_CRITERIA_OPTIONS = [
    'All Jobs Completed By',
    'Same Day',
    'Same Day + 1 Day',
    'Last 24 Hrs Till SLA Time',
    'Same Day (At Least Once) Till SLA Time',
    'All Jobs Completed in Same Day'
];

const FLOW_DEFAULT_COLUMN_NAMES = [
    'FLOW SCHEDULE FREQUENCY',
    'DAYS',
    'SLA CRITERIA',
    'FINAL SLA TARGET (CST)',
    'ACTIONS'
];

const HOP_DEFAULT_COLUMN_NAMES = [
    'HOPS',
    'HOP SCHEDULE FREQUENCY',
    'DAYS',
    'SLA START TIME (CST)',
    'SLA CRITERIA',
    'SLA END TIME (CST)',
    'EXPECTED DURATION',
    'ACTIONS'
];

interface FlowTableRow extends FlowSlaData {
    rowIndex: number;
}

interface HopTableRow extends HopData {
    rowIndex: number;
}

interface FormatterProps<T> {
    row: T;
}

interface SlaColumn<T> {
    key: string;
    name: string;
    resizable?: boolean;
    minWidth?: number;
    width?: number;
    formatter?: ({ row }: FormatterProps<T>) => React.ReactNode;
}

const SLAs: React.FC<SLAsProps> = ({ onValidityChange, onDataChange }) => {
    const theme = useTheme();
    const colors = theme.palette;

    const [flowSlaEnabled, setFlowSlaEnabled] = useState(false);
    const [hopSlaEnabled, setHopSlaEnabled] = useState(true);

    const [flowSlaData, setFlowSlaData] = useState<FlowSlaData[]>([
        {
            frequency: 'Daily',
            days: 'All Days',
            criteria: 'All Jobs Completed in Same Day',
            target: '1010'
        }
    ]);

    const [hopData, setHopData] = useState<HopData[]>([
        {
            hopName: '1. Demo',
            scheduleFrequency: 'Daily',
            days: 'All Days',
            startTime: '00:10',
            criteria: 'All Jobs Completed in Same Day',
            endTime: '10:10',
            duration: '10 Hrs',
            isEditing: false
        },
        {
            hopName: '2. Test',
            scheduleFrequency: 'Weekly',
            days: 'Thu, Fri',
            startTime: '00:00',
            criteria: 'Last 24 Hrs Till SLA Time',
            endTime: '01:00',
            duration: '10 Hrs',
            isEditing: false
        }
    ]);

    useEffect(() => {
        onValidityChange(true);
        onDataChange({
            hops: hopData,
            flows: flowSlaData
        });
    }, [
        hopData,
        flowSlaData,
        onValidityChange,
        onDataChange
    ]);

    const handleRemoveFlowRow = (indexToRemove: number) => {
        setFlowSlaData((previousData) =>
            previousData.filter(
                (_, index) => index !== indexToRemove
            )
        );
    };

    const handleHopDataChange = <K extends keyof HopData>(
        index: number,
        field: K,
        value: HopData[K]
    ) => {
        setHopData((previousData) =>
            previousData.map((hop, hopIndex) =>
                hopIndex === index
                    ? {
                        ...hop,
                        [field]: value
                    }
                    : hop
            )
        );
    };

    const handleAddHopRow = () => {
        setHopData((previousData) => [
            ...previousData,
            {
                hopName: `${previousData.length + 1}. New Hop`,
                scheduleFrequency: 'Daily',
                days: 'All Days',
                startTime: '00:00',
                criteria: 'Same Day',
                endTime: '01:00',
                duration: '1 Hr',
                isEditing: true
            }
        ]);
    };

    const handleRemoveHopRow = (indexToRemove: number) => {
        setHopData((previousData) =>
            previousData.filter(
                (_, index) => index !== indexToRemove
            )
        );
    };

    const handleToggleHopEdit = (indexToToggle: number) => {
        setHopData((previousData) =>
            previousData.map((hop, index) =>
                index === indexToToggle
                    ? {
                        ...hop,
                        isEditing: !hop.isEditing
                    }
                    : hop
            )
        );
    };

    const clockAdornment = (
        <InputAdornment position="end">
            <AccessTimeIcon
                sx={{
                    color: colors.inputNew.inputIcon,
                    fontSize: '1.2rem'
                }}
            />
        </InputAdornment>
    );

    const selectSx = {
        width: '100%',
        margin: '0 !important',
        backgroundColor: colors.inputNew.inputBackground,
        color: colors.inputNew.inputText,
        borderRadius: '4px',

        '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            color: `${colors.inputNew.inputText} !important`,
            padding: '8px 36px 8px 12px !important'
        },

        '& .MuiOutlinedInput-notchedOutline': {
            borderColor:
                `${colors.inputNew.inputBorder} !important`
        },

        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor:
                `${colors.inputNew.inputBorderHover} !important`
        },

        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor:
                `${colors.inputNew.inputBorderFocus} !important`
        },

        '& .MuiSelect-icon': {
            color:
                `${colors.DropdownV2.dropdownIconDefault} !important`
        }
    };

    const inputContainerSx = {
        width: '100%',
        margin: '0 !important',
        padding: '0 !important',

        '& .MuiInputBase-root': {
            width: '100%',
            margin: '0 !important',
            backgroundColor:
                `${colors.inputNew.inputBackground} !important`,
            color:
                `${colors.inputNew.inputText} !important`,
            borderColor:
                `${colors.inputNew.inputBorder} !important`,
            borderRadius: '4px !important',
            boxShadow: 'none !important'
        },

        '& .MuiOutlinedInput-root': {
            margin: '0 !important',
            boxShadow: 'none !important'
        },

        '& .MuiOutlinedInput-notchedOutline': {
            borderColor:
                `${colors.inputNew.inputBorder} !important`
        },

        '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor:
                `${colors.inputNew.inputBorderHover} !important`
        },

        '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor:
                `${colors.inputNew.inputBorderFocus} !important`
        },

        '& input': {
            color:
                `${colors.inputNew.inputText} !important`,
            WebkitTextFillColor:
                `${colors.inputNew.inputText} !important`,
            margin: '0 !important'
        },

        '& .MuiInputAdornment-root': {
            marginLeft: '0 !important'
        },

        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
            color:
                `${colors.inputNew.inputIcon} !important`
        }
    };

    const menuProps = {
        PaperProps: {
            sx: {
                mt: 1,
                overflow: 'hidden',
                borderRadius: '4px',
                color:
                    colors.DropdownV2.menuItemText,

                '& .MuiMenu-list': {
                    p: 0,
                    backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
                },

                '& .MuiMenuItem-root': {
                    minHeight: '40px',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    color:
                        `${colors.DropdownV2.menuItemText} !important`,
                    backgroundColor:
                        `${colors.DropdownV2.menuItemBackground} !important`
                },

                '& .MuiMenuItem-root:hover': {
                    color:
                        `${colors.DropdownV2.menuItemHoverText} !important`,
                    backgroundColor:
                        `${colors.DropdownV2.menuItemHover} !important`
                },

                '& .MuiMenuItem-root.Mui-selected': {
                    color:
                        `${colors.DropdownV2.menuItemSelectedText} !important`,
                    backgroundColor:
                        `${colors.DropdownV2.menuItemSelectedBg} !important`
                },

                '& .MuiMenuItem-root.Mui-selected:hover': {
                    color:
                        `${colors.DropdownV2.menuItemSelectedText} !important`,
                    backgroundColor:
                        `${colors.DropdownV2.menuItemSelectedBg} !important`
                }
            }
        }
    };

    const flowTableData: FlowTableRow[] =
        flowSlaData.map((flow, index) => ({
            ...flow,
            rowIndex: index
        }));

    const hopTableData: HopTableRow[] =
        hopData.map((hop, index) => ({
            ...hop,
            rowIndex: index
        }));

    const renderText = (value: string) => (
        <Typography
            variant="tableRowFont"
            sx={{
                color: colors.inputNew.inputText
            }}
        >
            {value}
        </Typography>
    );

    const renderSelect = (
        rowIndex: number,
        field: 'scheduleFrequency' | 'days' | 'criteria',
        value: string,
        options: string[],
        isEditing: boolean
    ) => {
        if (!isEditing) {
            return renderText(value);
        }

        return (
            <Box
                sx={{
                    width: '100%',
                    margin: 0,
                    padding: 0
                }}
            >
                <Select
                    onChange={(event) =>
                        handleHopDataChange(
                            rowIndex,
                            field,
                            event.target.value
                        )
                    }
                    MenuProps={menuProps}
                    value={value}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={selectSx}
                    renderValue={(selectedValue) => (
                        <Typography
                            variant="tableRowFont"
                            sx={{
                                color: colors.inputNew.inputText
                            }}
                        >
                            {selectedValue}
                        </Typography>
                    )}
                >
                    {options.map((option) => {
                        const isSelected = option === value;

                        return (
                            <MenuItem
                                key={option}
                                value={option}
                            >
                                <Typography
                                    variant="tableRowFont"
                                    sx={{
                                        color: 'inherit'
                                    }}
                                >
                                    {option}
                                </Typography>

                                {isSelected && (
                                    <CheckIcon
                                        sx={{
                                            color:
                                                `${colors.DropdownV2.tickColor} !important`,
                                            fontSize: '18px',
                                            flexShrink: 0
                                        }}
                                    />
                                )}
                            </MenuItem>
                        );
                    })}
                </Select>
            </Box>
        );
    };

    const renderInput = (
        rowIndex: number,
        field: 'startTime' | 'endTime' | 'duration',
        value: string,
        isEditing: boolean,
        showClock = false
    ) => {
        if (!isEditing) {
            return renderText(value);
        }

        return (
            <Box sx={inputContainerSx}>
                <StyledInput
                    onChange={(event) =>
                        handleHopDataChange(
                            rowIndex,
                            field,
                            event.target.value
                        )
                    }
                    value={value}
                    endAdornment={
                        showClock
                            ? clockAdornment
                            : undefined
                    }
                />
            </Box>
        );
    };

    const renderHopActions = (row: HopTableRow) => (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            {row.isEditing ? (
                <>
                    <IconButton
                        onClick={() =>
                            handleToggleHopEdit(row.rowIndex)
                        }
                        size="small"
                    >
                        <CloseIcon
                            sx={{
                                color: colors.inputNew.removeIcon
                            }}
                        />
                    </IconButton>

                    <IconButton
                        onClick={() =>
                            handleToggleHopEdit(row.rowIndex)
                        }
                        size="small"
                    >
                        <CheckIcon
                            sx={{
                                color:
                                    colors.inputNew.saveBackground
                            }}
                        />
                    </IconButton>
                </>
            ) : (
                <>
                    <IconButton
                        onClick={() =>
                            handleToggleHopEdit(row.rowIndex)
                        }
                        size="small"
                    >
                        <EditIcon
                            sx={{
                                color: colors.inputNew.inputIcon
                            }}
                        />
                    </IconButton>

                    <IconButton
                        onClick={() =>
                            handleRemoveHopRow(row.rowIndex)
                        }
                        size="small"
                    >
                        <DeleteIcon
                            sx={{
                                color: colors.inputNew.removeIcon
                            }}
                        />
                    </IconButton>
                </>
            )}
        </Box>
    );

    const getFlowColumns = (): SlaColumn<FlowTableRow>[] => [
        {
            key: 'frequency',
            name: 'FLOW SCHEDULE FREQUENCY',
            resizable: true,
            minWidth: 190
        },
        {
            key: 'days',
            name: 'DAYS',
            resizable: true,
            minWidth: 150
        },
        {
            key: 'criteria',
            name: 'SLA CRITERIA',
            resizable: true,
            minWidth: 300
        },
        {
            key: 'target',
            name: 'FINAL SLA TARGET (CST)',
            resizable: true,
            minWidth: 190
        },
        {
            key: 'actions',
            name: 'ACTIONS',
            resizable: false,
            width: 80,
            formatter: ({ row }) => (
                <Box
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <IconButton
                        onClick={() =>
                            handleRemoveFlowRow(row.rowIndex)
                        }
                        size="small"
                    >
                        <DeleteIcon
                            sx={{
                                color: colors.inputNew.removeIcon
                            }}
                        />
                    </IconButton>
                </Box>
            )
        }
    ];

    const getHopColumns = (): SlaColumn<HopTableRow>[] => [
        {
            key: 'hopName',
            name: 'HOPS',
            resizable: true,
            minWidth: 155
        },
        {
            key: 'scheduleFrequency',
            name: 'HOP SCHEDULE FREQUENCY',
            resizable: true,
            minWidth: 210,
            formatter: ({ row }) =>
                renderSelect(
                    row.rowIndex,
                    'scheduleFrequency',
                    row.scheduleFrequency,
                    SCHEDULE_FREQUENCY_OPTIONS,
                    row.isEditing
                )
        },
        {
            key: 'days',
            name: 'DAYS',
            resizable: true,
            minWidth: 125,
            formatter: ({ row }) =>
                renderSelect(
                    row.rowIndex,
                    'days',
                    row.days,
                    DAYS_OPTIONS,
                    row.isEditing
                )
        },
        {
            key: 'startTime',
            name: 'SLA START TIME (CST)',
            resizable: true,
            minWidth: 165,
            formatter: ({ row }) =>
                renderInput(
                    row.rowIndex,
                    'startTime',
                    row.startTime,
                    row.isEditing,
                    true
                )
        },
        {
            key: 'criteria',
            name: 'SLA CRITERIA',
            resizable: true,
            minWidth: 140,
            formatter: ({ row }) =>
                renderSelect(
                    row.rowIndex,
                    'criteria',
                    row.criteria,
                    SLA_CRITERIA_OPTIONS,
                    row.isEditing
                )
        },
        {
            key: 'endTime',
            name: 'SLA END TIME (CST)',
            resizable: true,
            minWidth: 165,
            formatter: ({ row }) =>
                renderInput(
                    row.rowIndex,
                    'endTime',
                    row.endTime,
                    row.isEditing,
                    true
                )
        },
        {
            key: 'duration',
            name: 'EXPECTED DURATION',
            resizable: true,
            minWidth: 140,
            formatter: ({ row }) =>
                renderInput(
                    row.rowIndex,
                    'duration',
                    row.duration,
                    row.isEditing
                )
        },
        {
            key: 'actions',
            name: 'ACTIONS',
            resizable: false,
            width: 80,
            formatter: ({ row }) =>
                renderHopActions(row)
        }
    ];

    return (
        <Box
            sx={{
                py: 4,
                px: 0,
                backgroundColor:
                    colors.dataPipeline.accordionWidget.background.page,
                color:
                    colors.dataPipeline.accordionWidget.text.primary
            }}
        >
            <Typography variant="body1" sx={{ mb: 2 }}>
                ⓘ This section is to configure SLA
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <Typography
                    variant="subHeader"
                    sx={{
                        color:
                            colors.dataPipeline.accordionWidget.text.link
                    }}
                >
                    SLA Configurations
                </Typography>

                <Box
                    sx={{
                        flexGrow: 1,
                        height: '1px',
                        ml: 1,
                        backgroundColor:
                            colors.dataPipeline.accordionWidget.border
                                .expandedAccordion
                    }}
                />
            </Box>

            <Box
                sx={{
                    display:'flex',
                    flexDirection:'column',
                    alignItems:'flex-start'
                }}
            >
                <FormControlLabel
                    sx={{
                        mt: 2,
                        ml: 0,
                        gap: '6px',
                        '& .MuiFormControlLabel-label': {
                            marginLeft: 0,
                            color: colors.toggle.text
                        }
                    }}
                    control={
                        <AntSwitch
                            checked={flowSlaEnabled}
                            onChange={(event) => setFlowSlaEnabled(event.target.checked)}
                            sx={{
                                m: 0,
                                '& .MuiSwitch-switchBase': {
                                    color: colors.toggle.thumb
                                },
                                '& .MuiSwitch-thumb': {
                                    backgroundColor: colors.toggle.thumb,
                                    boxShadow: colors.toggle.thumbShadow
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: colors.toggle.trackOff,
                                    opacity: 1
                                },
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: colors.toggle.thumb
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: colors.toggle.trackOn,
                                    opacity: 1
                                },
                                '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                                    backgroundColor: colors.toggle.disabledTrack,
                                    opacity: 1
                                }
                            }}
                        />
                    }
                    label={
                        <Typography variant="subHeader">
                            Configure Flow/Program Level SLA.
                        </Typography>
                    }
                />

                {flowSlaEnabled && (
                    <>
                        <Box
                            mt={1}
                            sx={{
                                width: '100%'
                            }}
                        >
                            <CustomizedDataGrid
                                tableColumns={getFlowColumns()}
                                tableData={flowTableData}
                                tableType="sla-flow-configuration"
                                defaultColumnNames={
                                    FLOW_DEFAULT_COLUMN_NAMES
                                }
                                listLength={flowTableData.length}
                                isFetching={false}
                                isRenderPagination={false}
                                isShowMoreButton={false}
                                autoSetDraggedColumns
                                isLighterTheme
                                isDateRangeFilter={false}
                                isDisabled={false}
                                showExport={false}
                            />
                        </Box>

                        <Typography
                            variant="labelText"
                            onClick={() => { }}
                            sx={{
                                color: colors.inputNew.inputPlaceholder,
                                cursor: 'pointer',
                                mt: 1,
                                display: 'flex',
                                alignItems: 'center',
                                width: 'fit-content'
                            }}
                        >
                            <AddIcon sx={{ mr: 1 }} />
                            Add Row
                        </Typography>
                    </>
                )}

                <FormControlLabel
                    sx={{
                        mt: 4,
                        ml: 0,
                        gap: '6px',
                        '& .MuiFormControlLabel-label': {
                            marginLeft: 0,
                            color: colors.toggle.text
                        }
                    }}
                    control={
                        <AntSwitch
                            checked={hopSlaEnabled}
                            onChange={(event) => setHopSlaEnabled(event.target.checked)}
                            sx={{
                                m: 0,
                                '& .MuiSwitch-switchBase': {
                                    color: colors.toggle.thumb
                                },
                                '& .MuiSwitch-thumb': {
                                    backgroundColor: colors.toggle.thumb,
                                    boxShadow: colors.toggle.thumbShadow
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: colors.toggle.trackOff,
                                    opacity: 1
                                },
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: colors.toggle.thumb
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: colors.toggle.trackOn,
                                    opacity: 1
                                },
                                '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                                    backgroundColor: colors.toggle.disabledTrack,
                                    opacity: 1
                                }
                            }}
                        />
                    }
                    label={
                        <Typography variant="subHeader">
                            Configure Hop Level SLA.
                        </Typography>
                    }
                />

                {hopSlaEnabled && (
                    <>
                        <Box
                            mt={1}
                        >
                            <CustomizedDataGrid
                                tableColumns={getHopColumns()}
                                tableData={hopTableData}
                                tableType="sla-hop-configuration"
                                defaultColumnNames={
                                    HOP_DEFAULT_COLUMN_NAMES
                                }
                                listLength={hopTableData.length}
                                isFetching={false}
                                isRenderPagination={false}
                                isShowMoreButton={false}
                                autoSetDraggedColumns
                                isLighterTheme
                                isDateRangeFilter={false}
                                isDisabled={false}
                                showExport={false}
                            />
                        </Box>

                        <Typography
                            variant="labelText"
                            onClick={handleAddHopRow}
                            sx={{
                                color: colors.inputNew.addText,
                                cursor: 'pointer',
                                mt: 2,
                                display: 'flex',
                                alignItems: 'center',
                                width: 'fit-content'
                            }}
                        >
                            <AddIcon sx={{ mr: 1 }} />
                            Add Row
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    );
};


// --- Alerts Component ---
interface AlertFlow {
    id?: number | null;
    deliveryChannel: string;
    alertType: string;
    channel: string;
    recipient: string;
    isEditing: boolean;
    Alert_ID?: string;
    emailSubject?: string;
    emailBody?: string;
    templateName?: string;
    serviceNowUrl?: string;
    payload?: any;
    title?: string;
    body?: string;
    webhookUrl?: string;
    teamsMail?: string;
}

interface AlertHop {
    id?: number | null;
    projects: string;
    alertType: string;
    channel: string;
    recipient: string;
    isEditing: boolean;
    Alert_ID?: string;
    HopName?: string;
    emailSubject?: string;
    emailBody?: string;
    templateName?: string;
    payload?: any;
    serviceNowUrl?: string;
    title?: string;
    body?: string;
    webhookUrl?: string;
    teamsMail?: string;
}

interface AlertFlowTableRow extends AlertFlow {
    rowIndex: number;
}

interface AlertHopTableRow extends AlertHop {
    rowIndex: number;
}

const FLOW_DEFAULT_COLUMNS = [
    'DELIVERY CHANNEL',
    'ALERT TYPE',
    'RECIPIENT/URL',
    'ACTIONS'
];

const HOP_DEFAULT_COLUMNS = [
    'HOP',
    'ALERT TYPE',
    'CHANNELS',
    'RECIPIENT/URL',
    'ACTIONS'
];

const ALERT_TYPE_OPTIONS = [
    'SLA Missed',
    'SLA Met',
    'SLA Missed, SLA Met'
];

const Alerts: React.FC<{
    formDataDashboard: any;
    hops: any[];
    programId: any;
    onAlertCreated?: (alertId: string) => void;
}> = ({ formDataDashboard, hops, programId, onAlertCreated }) => {
    const theme = useTheme();
    const colors = theme.palette;

    const [showAddForm, setShowAddForm] = useState<string | null>(null);
    const [editingIndex, setEditingIndex] = useState<{ level: 'flow' | 'hop', index: number } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [flowAlertsEnabled, setFlowAlertsEnabled] = useState(false);
    const [hopAlertsEnabled, setHopAlertsEnabled] = useState(true);
    // Error state for user feedback
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });

    const showError = (message: string) => {
        setErrorDialog({ open: true, message });
    };

    const initialAlertData = {
        hopName: '',
        alertType: '',
        channel: 'Email',
        templateName: '',
        emailBody: '',
        recipients: '',
        deliveryChannel: '',
        projects: [] as string[],

        // Email config
        emailSubject: '',
        
        // Service Now config
        payload: {
            description: '',
            ConfigurationItem: '',
            resource: '',
            metric_name: '',
            AssignmentGroup: '',
            severity: '2',
            source: 'PepWise',
            type: 'incident'
        },
        serviceNowUrl: '',
        
        // Teams config
        title: '',
        body: '',
        webhookUrl: '',
        teamsMail: ''
    };

    const [newAlertData, setNewAlertData] = useState(initialAlertData);
    const [flowAlerts, setFlowAlerts] = useState<AlertFlow[]>([]);
    const [hopAlerts, setHopAlerts] = useState<AlertHop[]>([]);

    // --- START: API Integration Functions ---

    // Get programName from props
    const programName = formDataDashboard.dashboardName;

    // Modify fetchAlerts to not take an argument
    const fetchAlerts = async () => {
        setIsLoading(true);
        try {
            // Get program name from dashboard data
            // const programName = formDataDashboard.dashboardName; // Now defined outside
            if (!programName) {
                console.warn("Program name not available, cannot fetch alerts");
                setFlowAlerts([]);
                setHopAlerts([]);
                setIsLoading(false); // Stop loading if no program name
                return;
            }

            // Fetch alert configurations from CosmosDB
            const alertConfigsResponse: any = await getAlertConfigsByProgram(programName);
            
            if (!alertConfigsResponse.success || !alertConfigsResponse.data?.data) {
                console.warn("No alert configs found or API failed");
                setFlowAlerts([]);
                setHopAlerts([]);
                setIsLoading(false); // Stop loading on failure
                return;
            }

            const alertConfigs = alertConfigsResponse.data.data.data;
            console.log("Fetched alert configs from CosmosDB:", alertConfigs);

            // Separate flow level and hop level alerts based on project name pattern
            const flowAlerts: AlertFlow[] = [];
            const hopAlerts: AlertHop[] = [];

             // Ensure alertConfigs is an array
            const configsArray = Array.isArray(alertConfigs) ? alertConfigs : [];
            
            configsArray.forEach((config: any) => {
                // Determine if it's a flow level or hop level alert based on the project field
                const isFlowLevel = config.project === programName;
                const isHopLevel = !isFlowLevel && config.project !== programName;

                // Extract channel and recipient information
                let channel = 'Email'; // default
                let recipientValue = '';
                let templateName = '';
                let emailSubject = '';
                let emailBody = '';
                let serviceNowUrl = '';
                let title = '';
                let body = '';
                let webhookUrl = '';
                let teamsMail = '';
                let payload = { ...initialAlertData.payload };

                // Parse different channel configurations
                if (config.emailConfig?.isEnabled) {
                    recipientValue = config.emailConfig.emailRecipients?.join(', ') || '';
                    templateName = config.emailConfig.templateName || '';
                    emailSubject = config.emailConfig.emailSubject || '';
                    emailBody = config.emailConfig.htmlTemplate || '';
                } else if (config.serviceNowConfig?.isEnabled) {
                    channel = 'SNOW';
                    recipientValue = config.serviceNowConfig.serviceNowUrl || '';
                    templateName = config.serviceNowConfig.templateName || '';
                    serviceNowUrl = config.serviceNowConfig.serviceNowUrl || '';
                    if (config.serviceNowConfig.payload?.data) {
                        payload = { ...initialAlertData.payload, ...config.serviceNowConfig.payload.data };
                    }
                } else if (config.teamsConfig?.isEnabled) {
                    channel = 'MS Teams';
                    recipientValue = config.teamsConfig.webhookUrl || config.teamsConfig.teamsMail || '';
                    templateName = config.teamsConfig.templateName || '';
                    title = config.teamsConfig.title || '';
                    body = config.teamsConfig.bodyTemplate || '';
                    webhookUrl = config.teamsConfig.webhookUrl || '';
                    teamsMail = config.teamsConfig.teamsMail || '';
                }

                if (isFlowLevel) {
                    flowAlerts.push({
                        id: null, // No SQL ID for CosmosDB records
                        deliveryChannel: channel,
                        alertType: config.alertType || 'SLA Status',
                        channel: channel,
                        recipient: recipientValue,
                        isEditing: false,
                        Alert_ID: config._id, // CosmosDB ID
                        emailSubject,
                        emailBody,
                        templateName,
                        payload,
                        serviceNowUrl,
                        title,
                        body,
                        webhookUrl,
                        teamsMail
                    });
                } else if (isHopLevel) {
                    hopAlerts.push({
                        id: null, // No SQL ID for CosmosDB records
                        projects: config.project || '',
                        alertType: config.alertType || 'SLA Status',
                        channel,
                        recipient: recipientValue,
                        isEditing: false,
                        Alert_ID: config._id, // CosmosDB ID
                        HopName: config.project || '',
                        emailSubject,
                        emailBody,
                        templateName,
                        payload,
                        serviceNowUrl,
                        title,
                        body,
                        webhookUrl,
                        teamsMail
                    });
                }
            });

            setFlowAlerts(flowAlerts);
            setHopAlerts(hopAlerts);
            
        } catch (error) {
            console.error("Failed to fetch alerts:", error);
            setFlowAlerts([]);
            setHopAlerts([]);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Update useEffect to depend on `programName`
    useEffect(() => {
        // Only fetch alerts if we have a programName
        if (programName) {
            fetchAlerts();
        }
    }, [programName]); // Depend on programName
    // --- END: API Integration Functions ---

    const validateProgramId = (progId?: number) => {
        if (!progId) {
            showError("Program ID is missing. Please ensure a Program is selected in the 'Workflow Configuration' tab.");
            throw new Error("Program ID is missing."); 
        }
    };

    const resolveProjectNames = (
        level: 'flow' | 'hop',
        programName: string,
        projects: string[]
    ) => {
        if (level !== 'hop') {
            return {
                projectNameOrHopList: programName,
                projectForRuleName: 'FLOW'
            };
        }

        const isAllHops = projects.includes(ALL_HOPS_VALUE);

        return {
            projectNameOrHopList: isAllHops ? ALL_HOPS_LABEL : projects.join(', '),
            projectForRuleName: isAllHops ? 'ALL' : projects.join('_')
        };
    };

    const buildSqlConfig = (
        level: 'flow' | 'hop',
        progId: number,
        isEditMode: boolean,
        projects: string[]
    ) => {
        if (level === 'flow') {
            return {
                query: `SELECT coalesce(exctv_prgrm_frndly_nm, exctv_prgrm_nm) Program_Name, sts.sla_Stts as SLA_Status, sts.sched_dt as Scheduled_Date FROM [dataPipeline].[EXCTV_STTS] sts join dataPipeline.EXCTV_PRGRM prg on prg.exctv_prgrm_id = sts.exctv_id where sla_Stts IN ('SLA Met','SLA Missed') and sched_dt = CONVERT(DATE, SWITCHOFFSET(GETUTCDATE(), '-06:00')) and prg.exctv_prgrm_id = ${progId}`,
                duplicateCheckFields: ['Program_Name', 'SLA_Status', 'Scheduled_Date']
            };
        }

        // ---- HOP LEVEL ----
        if (isEditMode) {
            return {
                query: `SELECT proj.Project_Name, Schedule_Date, SLA_Status FROM datapipeline.ts_project_status ps JOIN dataPipeline.Project proj on proj.Project_Id=ps.Project_Id WHERE ps.program_id = ${progId} AND schedule_date = CONVERT(DATE, SWITCHOFFSET(GETUTCDATE(), '-06:00')) AND SLA_Status IN ('SLA Missed', 'SLA Met')`,
                duplicateCheckFields: ['Project_Name', 'Schedule_Date', 'SLA_Status']
            };
        }

        const selectedHops = projects.includes(ALL_HOPS_VALUE)
            ? hops
            : hops.filter(h => projects.includes(h.hopName));

        const projectIds = selectedHops
            .map(h => h.project?.Project_Id || h.hopDetails?.projectId)
            .filter(Boolean)
            .join(',');

        if (!projectIds) {
            showError("No valid project IDs found for the selected hops. Please check Workflow Configuration.");
            throw new Error("No valid project IDs for hops.");
        }

        return {
            query: `SELECT proj.Project_Name, Schedule_Date, SLA_Status FROM datapipeline.ts_project_status ps JOIN dataPipeline.Project proj on proj.Project_Id=ps.Project_Id WHERE ps.program_id = ${progId} and ps.Project_Id in (${projectIds}) AND schedule_date = CONVERT(DATE, SWITCHOFFSET(GETUTCDATE(), '-06:00')) AND SLA_Status IN ('SLA Missed', 'SLA Met')`,
            duplicateCheckFields: ['Project_Name', 'Schedule_Date', 'SLA_Status']
        };
    };

    const buildEmailConfig = (programName: string, level: string) => ({
        isEnabled: true,
        templateName: newAlertData.templateName || "Default",
        emailSubject: newAlertData.emailSubject || `PEPWISE ${programName} Reporting - SLA Status`,
        htmlTemplate: newAlertData.emailBody || `<p>SLA Status of {{${level === 'flow' ? 'Program_Name' : 'Project_Name'}}}: {{SLA_Status}}</p>`,
        emailRecipients: newAlertData.recipients
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
        channel: "Email"
    });

    const buildSnowConfig = (level: string) => ({
        isEnabled: true,
        templateName: newAlertData.templateName || "Default",
        payload: {
            data: {
                description: newAlertData.payload.description || `SLA Status of {{${level === 'flow' ? 'Program_Name' : 'Project_Name'}}}: {{SLA_Status}}`,
                ConfigurationItem: newAlertData.payload.ConfigurationItem || "",
                resource: newAlertData.payload.resource || "",
                metric_name: newAlertData.payload.metric_name || "",
                AssignmentGroup: newAlertData.payload.AssignmentGroup || "",
                severity: newAlertData.payload.severity || "2",
                source: "PepWise",
                type: "incident"
            }
        },
        serviceNowUrl: newAlertData.serviceNowUrl || '',
        channel: 'Service Now'
    });

    const buildTeamsConfig = (programName: string, level: string) => ({
        title: newAlertData.title || `PEPWISE ${programName} Reporting - SLA Status`,
        templateName: newAlertData.templateName || "Default",
        bodyTemplate: newAlertData.body || `SLA Status of {{${level === 'flow' ? 'Program_Name' : 'Project_Name'}}}: {{SLA_Status}}`,
        webhookUrl: newAlertData.webhookUrl || "",
        teamsMail: newAlertData.teamsMail || "",
        isEnabled: true,
        channel: "Teams"
    });

    const buildAlertConfig = (level: 'flow' | 'hop', isEditMode = false) => {
        const programName = formDataDashboard.dashboardName;
        const sector = formDataDashboard.sector?.Sector_Name || formDataDashboard.sector;
        const progId = programId; // Use passed programId (which now comes from workflowData)

        validateProgramId(progId);

        // Determine project names and IDs
        const { projectNameOrHopList, projectForRuleName } =
            resolveProjectNames(level, programName, newAlertData.projects);

        // Build ruleName
        const ruleName = `${programName}-${projectForRuleName}-${sector}-SLA-ALERT`.replace(/\s/g, '_');

        // Build SQL query based on level
        const { query, duplicateCheckFields } = buildSqlConfig(
            level,
            progId,
            isEditMode,
            newAlertData.projects
        );

        // Build base config
        const config: any = {
            ruleName,
            alertStore: true,
            alertType: 'SLA Status',
            pepWiseAlertType: 'ALERT',
            isNewDataModel: true,
            sourceConfig: {
                sourceType: 'sql',
                query,
                duplicateCheckFields
            },
            sector,
            project: projectNameOrHopList,
            program: programName
        };

        // Add email config if Email channel selected
        if (newAlertData.channel === 'Email') {
            config.emailConfig = buildEmailConfig(programName, level);
        }

        // Add Service Now config if SNOW channel selected
        if (newAlertData.channel === 'SNOW') {
            config.serviceNowConfig = buildSnowConfig(level);
        }

        // Add Teams config if MS Teams channel selected
        if (newAlertData.channel === 'MS Teams') {
            config.teamsConfig = buildTeamsConfig(programName, level);
        }

        return config;
    };

    const resolveEditIds = (
        level: 'flow' | 'hop',
        editingIndex: any,
        flowAlerts: any[],
        hopAlerts: any[]
    ) => {
        if (!editingIndex) {
            return { alertId: undefined, sqlAlertId: null };
        }

        const source = level === 'flow' ? flowAlerts : hopAlerts;
        const alert = source[editingIndex.index];

        return {
            alertId: alert?.Alert_ID,
            sqlAlertId: alert?.id ?? null
        };
    };

    const validateAlertInputs = (
        level: 'flow' | 'hop',
        programName: string,
        sector: string,
        alertType: string,
        projects: string[]
    ) => {
        if (!programName) {
            showError("Dashboard Name is required. Please complete the Dashboard Details tab first.");
            setIsLoading(false); // Stop loading
            return;
        }
        if (!sector) {
            showError("Sector is required. Please select a sector in Dashboard Details.");
            setIsLoading(false); // Stop loading
            return;
        }
        if (!alertType) {
            showError("Alert Type is required. Please select an alert type.");
            setIsLoading(false); // Stop loading
            return;
        }
        if (level === 'hop' && projects.length === 0) {
            showError("Please select at least one hop for project-level alerts.");
            setIsLoading(false); // Stop loading
        }
    };

    const saveAlertConfigToCosmos = async (
        isEditMode: boolean,
        alertId: string | undefined,
        alertConfig: any
    ): Promise<string> => {
        if (isEditMode && alertId) {
            const res = await updateAlertConfigs(alertId, alertConfig);
            if (!res.success) {
                throw new Error("Failed to update alert configuration in Cosmos DB");
            }
            return alertId;
        }

        const res = await createAlertConfig(alertConfig);
        const newId = res?.data?.data?._id;

        if (!res.success || !newId) {
            throw new Error("Failed to create alert configuration in Cosmos DB");
        }

        return newId;
    };

    const resolveInviteUsers = (channel: string, data: any): string => {
        switch (channel) {
            case 'Email':
                return JSON.stringify(
                    data.recipients
                        .split(',')
                        .map((s: string) => s.trim())
                        .filter(Boolean)
                );
            case 'MS Teams':
                return data.teamsMail;
            case 'SNOW':
                return data.serviceNowUrl;
            default:
                return '';
        }
    };

    const buildDeliveryPayload = (
        alertId: string,
        level: 'flow' | 'hop',
        progId: number,
        projectNameOrHopList: string,
        sqlAlertId: number | null,
        newAlertData: any
    ) => {
        const deliveryPayload: any = {
            Alert_ID: alertId,
            Type: level === 'flow' ? 'Work Flow' : 'Project Level',
            DeliveryChannel: newAlertData.channel,
            AlertType: newAlertData.alertType,
            TeamChannel: newAlertData.channel,
            InviteUsers: resolveInviteUsers(newAlertData.channel, newAlertData), // This will be email array, webhook, or SNOW URL
            TemplateName: newAlertData.templateName || 'Default',

            // *** THIS IS THE CRITICAL FIX ***
            ProjectId: progId,
            Project: projectNameOrHopList, // Use the correct project/hop name
            // ******************************

            EmailSubject: newAlertData.emailSubject,
            EmailBody: newAlertData.emailBody,
            Payload: JSON.stringify(newAlertData.payload),
            ServiceNowURL: newAlertData.serviceNowUrl,
            Title: newAlertData.title,
            Body: newAlertData.body,
            WebhookURL: newAlertData.webhookUrl,
            TeamsMail: newAlertData.teamsMail
        };

        if (level === 'hop') {
            deliveryPayload.HopName = projectNameOrHopList;
        }

        if (sqlAlertId) {
            deliveryPayload.id = sqlAlertId;
        }

        return deliveryPayload;
    };

    const handleSaveAlert = async () => {
        const level = showAddForm as 'flow' | 'hop';
        if (!level) return;

        const programName = formDataDashboard.dashboardName;
        const sector = formDataDashboard.sector?.Sector_Name || formDataDashboard.sector;
        const alertType = newAlertData.alertType;
        const progId = programId; // Use the prop passed from ExecutiveTemplate

        if (!progId) {
            showError("Program ID is missing. Please ensure a Program is selected in the 'Workflow Configuration' tab.");
            return; // Stop the function here
        }

        const isEditMode = editingIndex !== null;
        const { alertId: existingAlertId, sqlAlertId } = resolveEditIds(
            level,
            editingIndex,
            flowAlerts,
            hopAlerts
        );

        try {
            setIsLoading(true);

            validateAlertInputs(
                level,
                programName,
                sector,
                alertType,
                newAlertData.projects
            );

            const alertConfig = buildAlertConfig(level, isEditMode);

            const alertId = await saveAlertConfigToCosmos(
                isEditMode,
                existingAlertId,
                alertConfig
            );

            if (!alertId) {
                throw new Error("Alert ID is missing after create/update operation.");
            }

            // Activate alert immediately when creating in EDIT mode (program already exists)
            if (progId && !isEditMode) {
                console.log(`Activating alert in edit mode: ${alertId}`);
                try {
                    await activateAlertConfig(alertId);
                    console.log(`Alert ${alertId} activated successfully`);
                } catch (activationError) {
                    console.error(`Failed to activate alert ${alertId}:`, activationError);
                    showError("Alert created but activation failed. You can activate it manually later.");
                }
            }
            // Track alert for CREATE mode activation (only if not already activated)
             else if (!isEditMode && onAlertCreated) {
                onAlertCreated(alertId);
            }

            const isAllHops = newAlertData.projects.includes(ALL_HOPS_VALUE);
            let projectNameOrHopList = programName;
            if (level === 'hop') {
                projectNameOrHopList = isAllHops
                    ? ALL_HOPS_LABEL
                    : newAlertData.projects.join(', ');
            }

            const deliveryPayload = buildDeliveryPayload(
                alertId,
                level,
                progId,
                projectNameOrHopList,
                sqlAlertId,
                newAlertData
            );

            console.log(isEditMode ? "Updating delivery channel record:" : "Creating delivery channel record:", deliveryPayload);

            // COMMENTED OUT: SQL delivery channel record is not needed since we're using CosmosDB
            // await addOrUpdateAlertSlaProject(deliveryPayload);

            handleCancelAdd();
            if (programId) await fetchAlerts();

        } catch (error: any) {
            showError(error?.message || "Failed to create alert. Please try again.");
            console.error("Alert creation error:", error);
        } finally {
            setIsLoading(false);
        }
    };


    const handleCancelAdd = () => {
        setShowAddForm(null);
        setEditingIndex(null);
        setNewAlertData(initialAlertData);
    };

    const handleAddRowClick = (level: 'flow' | 'hop') => {
        if (showAddForm === level && !editingIndex) { // Only cancel if it's an "add" form
            handleCancelAdd();
        } else {
            handleCancelAdd(); // Resets everything

            const defaultEmailSubject = `PEPWISE ${formDataDashboard.dashboardName || 'Program'} Reporting - SLA Status`;
            const defaultBodyTemplate = `<p>SLA Status of {{${level === 'flow' ? 'Program_Name' : 'Project_Name'}}}: {{SLA_Status}}</p>`;
            const defaultSnowDescription = `SLA Status of {{${level === 'flow' ? 'Program_Name' : 'Project_Name'}}}: {{SLA_Status}}`;
            const defaultTeamsTitle = `PEPWISE ${formDataDashboard.dashboardName || 'Program'} Reporting - SLA Status`;
            const defaultTeamsBody = `SLA Status of {{${level === 'flow' ? 'Program_Name' : 'Project_Name'}}}: {{SLA_Status}}`;

            setNewAlertData(prev => ({ 
                ...initialAlertData, 
                projects: [],
                emailSubject: defaultEmailSubject, // Pre-fill subject
                emailBody: defaultBodyTemplate,       // Pre-fill body
                payload: {
                    ...initialAlertData.payload,
                    description: defaultSnowDescription, // Pre-fill SNOW description
                },
                title: defaultTeamsTitle, // Pre-fill Teams title
                body: defaultTeamsBody,   // Pre-fill Teams body
            }));
            
            setShowAddForm(level);
        }
    };

    const handleEditClick = (index: number, level: 'flow' | 'hop') => {
        setEditingIndex({ index, level });
        setShowAddForm(level);

        // Pre-populate form data from the selected alert
        if (level === 'flow') {
            const alertToEdit = flowAlerts[index];
            setNewAlertData({
                ...initialAlertData, // Start with defaults
                channel: alertToEdit.channel,
                alertType: alertToEdit.alertType,
                projects: [], // Not for flow
                                
                // Pre-fill all fields from the fetched alert
                emailSubject: alertToEdit.emailSubject || `PEPWISE ${formDataDashboard.dashboardName} Reporting - SLA Status`,
                emailBody: alertToEdit.emailBody || '',
                recipients: alertToEdit.channel === 'Email' ? alertToEdit.recipient : '', // Only set for email
                templateName: alertToEdit.templateName || '',

                payload: alertToEdit.payload || initialAlertData.payload,
                serviceNowUrl: alertToEdit.serviceNowUrl || '', // <-- Use the specific field

                title: alertToEdit.title || '',
                body: alertToEdit.body || '',
                webhookUrl: alertToEdit.webhookUrl || '',
                teamsMail: alertToEdit.teamsMail || ''
            });
        } else {
            const alertToEdit = hopAlerts[index];
            const projectsArray = alertToEdit.projects.includes(ALL_HOPS_LABEL)
                ? [ALL_HOPS_VALUE]
                : alertToEdit.projects.split(',').map(p => p.trim());

            setNewAlertData({
                ...initialAlertData, // Start with defaults
                projects: projectsArray,
                channel: alertToEdit.channel,
                alertType: alertToEdit.alertType,
                                
                // Pre-fill all fields
                emailSubject: alertToEdit.emailSubject || `PEPWISE ${formDataDashboard.dashboardName} Reporting - SLA Status`,
                emailBody: alertToEdit.emailBody || '',
                recipients: alertToEdit.channel === 'Email' ? alertToEdit.recipient : '',
                templateName: alertToEdit.templateName || '',

                payload: alertToEdit.payload || initialAlertData.payload,
                serviceNowUrl: alertToEdit.serviceNowUrl || '', //  specific field

                title: alertToEdit.title || '',
                body: alertToEdit.body || '',
                webhookUrl: alertToEdit.webhookUrl || '',
                teamsMail: alertToEdit.teamsMail || ''
            });
        }
    };

    const handleRemoveFlowRow = async (indexToRemove: number) => {
        const alertId = flowAlerts[indexToRemove].Alert_ID; // Use CosmosDB Alert_ID
        if (alertId) {
            try {
                setIsLoading(true);
                await deleteAlertConfigById(alertId); // Delete from CosmosDB
                if (programId) {
                    await fetchAlerts(); // Update call
                }
            } catch (error) {
                console.error("Failed to delete alert:", error);
            } finally {
                setIsLoading(false);
            }
        }
    };
    const handleRemoveHopRow = async (indexToRemove: number) => {
        const alertId = hopAlerts[indexToRemove].Alert_ID; // Use CosmosDB Alert_ID
        if (alertId) {
            try {
                setIsLoading(true);
                await deleteAlertConfigById(alertId); // Delete from CosmosDB
                if (programId) {
                    await fetchAlerts(); // Update call
                }
            } catch (error) {
                console.error("Failed to delete alert:", error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const hopOptions: Option[] = [
        {
            label: ALL_HOPS_LABEL,
            value: ALL_HOPS_VALUE
        },
        ...hops.map((hop: any) => ({
            label: hop.hopName,
            value: hop.hopName
        }))
    ];

    let alertTitle = 'Add Projects & Alert Types';
    if (editingIndex) {
        alertTitle = 'Edit Alert';
    } else if (showAddForm === 'flow') {
        alertTitle = 'Flow Level Alert Setup';
    }

    const toggleSx = {
        '& .MuiSwitch-thumb': {
            backgroundColor: colors.toggle.thumb,
            boxShadow: colors.toggle.thumbShadow
        },
        '& .MuiSwitch-track': {
            backgroundColor: colors.toggle.trackOff,
            opacity: 1
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colors.toggle.trackOn,
            opacity: 1
        },
        '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
            backgroundColor: colors.toggle.disabledTrack,
            opacity: 1
        }
    };

    const alertMenuProps = {
        PaperProps: {
            sx: {
                mt: 1,
                overflow: 'hidden',
                backgroundColor: colors.DropdownV2.menuItemBackground,
                color: colors.DropdownV2.menuItemText,
                '& .MuiMenu-list': {
                    p: 0,
                    backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`
                },
                '& .MuiMenuItem-root': {
                    minHeight: '40px',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: `${colors.DropdownV2.menuItemText} !important`,
                    backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`
                },
                '& .MuiMenuItem-root:hover': {
                    color: `${colors.DropdownV2.menuItemHoverText} !important`,
                    backgroundColor: `${colors.DropdownV2.menuItemHover} !important`
                },
                '& .MuiMenuItem-root.Mui-selected': {
                    color: `${colors.DropdownV2.menuItemSelectedText} !important`,
                    backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`
                },
                '& .MuiMenuItem-root.Mui-selected:hover': {
                    color: `${colors.DropdownV2.menuItemSelectedText} !important`,
                    backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`
                }
            }
        }
    };

    const renderChannelAccordion = (
        channel: 'Email' | 'SNOW' | 'MS Teams',
        title: string,
        children: React.ReactNode
    ) => {
        const isEnabled = newAlertData.channel === channel;

        return (
            <Accordion
                expanded={isEnabled}
                disableGutters
                sx={{
                    boxShadow: 'none',
                    mb: 2,
                    backgroundColor: colors.accordion.bg,
                    '&:before': {
                        display: 'none'
                    },
                    '&.Mui-expanded': {
                        backgroundColor: `${colors.dataPipeline.accordionWidget.background.card} !important`
                    },
                    '& .MuiAccordionDetails-root': {
                        border: 'none !important',
                        backgroundColor: `${colors.dataPipeline.accordionWidget.background.page} !important`
                    },
                    '& + .MuiAccordion-root': {
                        mt: 2
                    }
                }}
            >
                <AccordionSummary
                    expandIcon={
                        <ExpandMoreIcon
                            sx={{
                                color: isEnabled
                                    ? colors.dataPipeline.accordionWidget.accordionHeader.expandIconActive
                                    : colors.dataPipeline.accordionWidget.accordionHeader.expandIconInactive
                            }}
                        />
                    }
                    sx={{
                        minHeight: '48px',
                        backgroundColor: colors.accordion.bg,
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            gap: 1
                        }
                    }}
                >
                    <AntSwitch
                        checked={isEnabled}
                        onChange={(event) => {
                            event.stopPropagation();

                            if (event.target.checked) {
                                setNewAlertData(prev => ({
                                    ...prev,
                                    channel
                                }));
                            }
                        }}
                        onClick={(event) => event.stopPropagation()}
                        sx={toggleSx}
                    />

                    <Typography
                        variant="labelText"
                        sx={{
                            color: colors.dataPipeline.accordionWidget.text.link
                        }}
                    >
                        {title}
                    </Typography>

                    <Box
                        sx={{
                            flexGrow: 1,
                            height: '1px',
                            ml: 1,
                            mr: 2,
                            backgroundColor: colors.dataPipeline.accordionWidget.border.expandedAccordion
                        }}
                    />
                </AccordionSummary>

                {isEnabled && (
                    <AccordionDetails
                        sx={{
                            p: 0,
                            backgroundColor: `${colors.dataPipeline.accordionWidget.background.page} !important`,
                            border: 'none !important',
                            outline: 'none',
                        }}
                    >
                        {children}
                    </AccordionDetails>
                )}
            </Accordion>
        );
    };

    const renderAddAlertForm = () => (
        <div style={{ marginTop: '16px' }}>
            <Paper
                sx={{ 
                    p: 3, 
                    backgroundColor: `${colors.dataPipeline.accordionWidget.background.page} !important`, 
                    color: `${colors.dataPipeline.accordionWidget.text.primary} !important`, 
                    border: 'none', 
                    borderRadius: '8px',
                    boxShadow: 'none'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2
                    }}
                >
                    <Typography
                        variant="subHeader"
                        sx={{
                            color: colors.dataPipeline.accordionWidget.text.link,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {alertTitle}
                    </Typography>

                    <Box
                        sx={{
                            flexGrow: 1,
                            height: '1px',
                            ml: 2,
                            mr: 2,
                            backgroundColor: colors.dataPipeline.accordionWidget.border.expandedAccordion
                        }}
                    />
                </Box>

                {/* <Typography variant="subtitle2" sx={{ color: colors.dataPipeline.accordionWidget.text.primary, mt: 1, fontWeight: 'normal' }}>
                    {showAddForm === 'flow' ? 'Select Alert Type' : 'Select Hop Name(s) & Alert Type'}
                </Typography>

                <Divider sx={{ backgroundColor: colors.dataPipeline.accordionWidget.border.subtle, mt: 1, mb: 2 }} /> */}

                <Box sx={{ display: 'flex', gap: 3, mt: 2, alignItems: 'flex-end' }}>
                    {showAddForm === 'hop' && (
                        <Box 
                            sx={{
                                width: '100%',
                                '& > div > .MuiPaper-root': {
                                    border: `1px solid ${colors.inputNew.inputBorder} !important`
                                },
                                '& > div > .MuiPaper-root:hover': {
                                    border: `1px solid ${colors.inputNew.inputBorderHover} !important`
                                }
                            }}
                        >
                            <SearchableSelectDropdown
                                label=""
                                placeholder="Select Hop(s)*"
                                options={hopOptions}
                                multiple
                                value={newAlertData.projects}
                                onChange={(next) => {
                                    let updatedValue = Array.isArray(next) ? [...next] : [];
                                    const includesAll = updatedValue.includes(ALL_HOPS_VALUE);
                                    const length = updatedValue.length - 1;
                                    const lastSelected = updatedValue[length];

                                    if (lastSelected === ALL_HOPS_VALUE) {
                                        const allHopNames = hops.map((hop: any) => hop.hopName);
                                        updatedValue = [ALL_HOPS_VALUE, ...allHopNames];
                                    } else if (includesAll && lastSelected !== ALL_HOPS_VALUE) {
                                        updatedValue = updatedValue.filter(v => v !== ALL_HOPS_VALUE);
                                    } else if (!includesAll && updatedValue.length === hops.length) {
                                        updatedValue = [ALL_HOPS_VALUE, ...updatedValue];
                                    }

                                    setNewAlertData(prev => ({
                                        ...prev,
                                        projects: updatedValue
                                    }));
                                }}
                                showSearch = {false}
                                showAllOption = {false}
                            />
                        </Box>
                    )}

                    <Select
                        fullWidth
                        value={newAlertData.alertType}
                        onChange={(e) =>
                            setNewAlertData(prev => ({
                                ...prev,
                                alertType: e.target.value
                            }))
                        }
                        displayEmpty
                        IconComponent={ExpandMoreIcon}
                        sx={{
                            height: '45px !important',
                            minHeight: '45px !important',
                            maxHeight: '45px !important',
                            backgroundColor: colors.inputNew.inputBackground,
                            color: colors.inputNew.inputText,
                            borderRadius: '4px',

                            '& .MuiSelect-select': {
                                height: '45px !important',
                                minHeight: '45px !important',
                                display: 'flex',
                                alignItems: 'center',
                                boxSizing: 'border-box',
                                padding: '0 40px 0 16px !important',
                                color: `${colors.inputNew.inputText} !important`
                            },

                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.inputNew.inputBorder} !important`
                            },

                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.inputNew.inputBorderHover} !important`
                            },

                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.inputNew.inputBorderFocus} !important`
                            },

                            '& .MuiSelect-icon': {
                                color: `${colors.DropdownV2.dropdownIconDefault} !important`,
                                right: '12px'
                            },

                            '&:hover .MuiSelect-icon': {
                                color: `${colors.DropdownV2.dropdownIconActive} !important`
                            },

                            '&.Mui-focused .MuiSelect-icon': {
                                color: `${colors.DropdownV2.dropdownIconActive} !important`
                            }
                        }}
                        renderValue={(selected) =>
                            selected || 'Select Alert*'
                        }
                        MenuProps={alertMenuProps}
                    >
                        {ALERT_TYPE_OPTIONS.map((option) => (
                            <MenuItem
                                key={option}
                                value={option}
                            >
                                <Typography
                                    variant="tableRowFont"
                                    sx={{
                                        color: 'inherit'
                                    }}
                                >
                                    {option}
                                </Typography>

                                {newAlertData.alertType === option && (
                                    <CheckIcon
                                        sx={{
                                            color: `${colors.DropdownV2.tickColor} !important`,
                                            fontSize: '18px'
                                        }}
                                    />
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <Typography variant="subtitle2" sx={{ color: colors.dataPipeline.accordionWidget.text.primary, mt: 4, fontWeight: 'normal' }}>
                    Configure Delivery Channel
                </Typography>

                <Divider sx={{ backgroundColor: colors.dataPipeline.accordionWidget.border.subtle, mt: 1, mb: 2 }} />

                <Box sx={{ mt: 2 }}>
                    {renderChannelAccordion(
                        'Email',
                        'Email Channel',
                        <Box sx={{ pt: 2, px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                fullWidth
                                label="Email Subject"
                                variant="outlined"
                                sx={whiteModalInputStyles}
                                value={newAlertData.emailSubject}
                                onChange={(e) => setNewAlertData(prev => ({ ...prev, emailSubject: e.target.value }))}
                            />

                            <TextField
                                fullWidth
                                label="Select Recipient Emails or DL"
                                variant="outlined"
                                sx={whiteModalInputStyles}
                                value={newAlertData.recipients}
                                onChange={(e) => setNewAlertData(prev => ({ ...prev, recipients: e.target.value }))}
                            />

                            <RichTextEditor
                                value={newAlertData.emailBody}
                                onChange={(value) => setNewAlertData(prev => ({ ...prev, emailBody: value }))}
                            />
                        </Box>
                    )}

                    {renderChannelAccordion(
                        'SNOW',
                        'Service Now Channel',
                        <Box sx={{ pt: 2, px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                fullWidth
                                label="Template Name"
                                variant="outlined"
                                sx={whiteModalInputStyles}
                                value={newAlertData.templateName}
                                onChange={(e) => setNewAlertData(prev => ({ ...prev, templateName: e.target.value }))}
                            />

                            <TextField
                                fullWidth
                                label="Description"
                                variant="outlined"
                                multiline
                                rows={4}
                                sx={whiteModalInputStyles}
                                value={newAlertData.payload.description}
                                onChange={(e) => setNewAlertData(prev => ({
                                    ...prev,
                                    payload: { ...prev.payload, description: e.target.value }
                                }))}
                            />

                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Configuration Item"
                                    variant="outlined"
                                    sx={whiteModalInputStyles}
                                    value={newAlertData.payload.ConfigurationItem}
                                    onChange={(e) => setNewAlertData(prev => ({
                                        ...prev,
                                        payload: { ...prev.payload, ConfigurationItem: e.target.value }
                                    }))}
                                />

                                <TextField
                                    fullWidth
                                    label="Resource"
                                    variant="outlined"
                                    sx={whiteModalInputStyles}
                                    value={newAlertData.payload.resource}
                                    onChange={(e) => setNewAlertData(prev => ({
                                        ...prev,
                                        payload: { ...prev.payload, resource: e.target.value }
                                    }))}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Metric Name"
                                    variant="outlined"
                                    sx={whiteModalInputStyles}
                                    value={newAlertData.payload.metric_name}
                                    onChange={(e) => setNewAlertData(prev => ({
                                        ...prev,
                                        payload: { ...prev.payload, metric_name: e.target.value }
                                    }))}
                                />

                                <TextField
                                    fullWidth
                                    label="Assignment Group"
                                    variant="outlined"
                                    sx={whiteModalInputStyles}
                                    value={newAlertData.payload.AssignmentGroup}
                                    onChange={(e) => setNewAlertData(prev => ({
                                        ...prev,
                                        payload: { ...prev.payload, AssignmentGroup: e.target.value }
                                    }))}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Severity"
                                    variant="outlined"
                                    sx={whiteModalInputStyles}
                                    value={newAlertData.payload.severity}
                                    onChange={(e) => setNewAlertData(prev => ({
                                        ...prev,
                                        payload: { ...prev.payload, severity: e.target.value }
                                    }))}
                                    placeholder="2"
                                />

                                <TextField
                                    fullWidth
                                    label="Service Now URL"
                                    variant="outlined"
                                    sx={whiteModalInputStyles}
                                    value={newAlertData.serviceNowUrl}
                                    onChange={(e) => setNewAlertData(prev => ({ ...prev, serviceNowUrl: e.target.value }))}
                                />
                            </Box>
                        </Box>
                    )}

                    {renderChannelAccordion(
                        'MS Teams',
                        'MS Teams Channel',
                        <Box sx={{ pt: 2, px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Template Name"
                                    variant="outlined"
                                    sx={whiteModalInputStyles}
                                    value={newAlertData.templateName}
                                    onChange={(e) => setNewAlertData(prev => ({ ...prev, templateName: e.target.value }))}
                                />

                                <TextField
                                    fullWidth
                                    label="Title"
                                    variant="outlined"
                                    sx={whiteModalInputStyles}
                                    value={newAlertData.title}
                                    onChange={(e) => setNewAlertData(prev => ({ ...prev, title: e.target.value }))}
                                />
                            </Box>

                            <TextField
                                fullWidth
                                label="Teams Mail"
                                variant="outlined"
                                sx={whiteModalInputStyles}
                                value={newAlertData.teamsMail}
                                onChange={(e) => setNewAlertData(prev => ({ ...prev, teamsMail: e.target.value }))}
                                required
                            />

                            <RichTextEditor
                                value={newAlertData.body}
                                onChange={(value) => setNewAlertData(prev => ({ ...prev, body: value }))}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
                    <Button onClick={handleCancelAdd} variant="pepwiseButtonText" sx={{ color: `${colors.buttons.primary.textColor} !important`}} >
                        CANCEL
                    </Button>

                    <Button
                        variant="pepwiseSecondary"
                        onClick={handleSaveAlert}
                    >
                        SAVE
                    </Button>
                </Box>
            </Paper>
        </div>
    );

    const showFlowSection = !!programId;

    const flowTableData: AlertFlowTableRow[] = flowAlerts.map((alert, index) => ({
        ...alert,
        rowIndex: index
    }));

    const hopTableData: AlertHopTableRow[] = hopAlerts.map((alert, index) => ({
        ...alert,
        rowIndex: index
    }));

    const renderRecipient = (recipient: string, channel: string) => {
        if (!recipient) {
            return null;
        }

        const isUrl = /^https?:\/\//i.test(recipient);

        if (isUrl || channel === 'SNOW') {
            return (
                <Typography
                    component="a"
                    href={recipient}
                    target="_blank"
                    rel="noreferrer"
                    variant="tableRowFont"
                    sx={{
                        color: colors.dataPipeline.accordionWidget.text.link,
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        maxWidth: '100%'
                    }}
                >
                    {recipient}
                </Typography>
            );
        }

        const recipientList = recipient
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);

        const visibleRecipients = recipientList.slice(0, 2);
        const remainingCount = recipientList.length - visibleRecipients.length;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                {visibleRecipients.map(item => (
                    <Chip
                        key={item}
                        label={item}
                        size="small"
                        sx={{
                            maxWidth: '150px',
                            backgroundColor: colors.inputNew.inputBackground,
                            color: colors.inputNew.inputText,
                            '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }
                        }}
                    />
                ))}

                {remainingCount > 0 && (
                    <Typography
                        variant="tableRowFont"
                        sx={{
                            color: colors.dataPipeline.accordionWidget.text.link,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        +{remainingCount} more..
                    </Typography>
                )}
            </Box>
        );
    };

    const getFlowColumns = () => [
        {
            key: 'deliveryChannel',
            name: 'DELIVERY CHANNEL',
            minWidth: 180
        },
        {
            key: 'alertType',
            name: 'ALERT TYPE',
            minWidth: 200
        },
        {
            key: 'recipient',
            name: 'RECIPIENT/URL',
            minWidth: 400,
            formatter: ({ row }: { row: AlertFlowTableRow }) =>
                renderRecipient(row.recipient, row.channel)
        },
        {
            key: 'actions',
            name: 'ACTIONS',
            width: 110,
            formatter: ({ row }: { row: AlertFlowTableRow }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <IconButton onClick={() => handleEditClick(row.rowIndex, 'flow')} size="small">
                        <EditIcon sx={{ color: colors.inputNew.inputIcon }} />
                    </IconButton>
                    <IconButton onClick={() => handleRemoveFlowRow(row.rowIndex)} size="small">
                        <DeleteIcon sx={{ color: colors.inputNew.removeIcon }} />
                    </IconButton>
                </Box>
            )
        }
    ];

    const getHopColumns = () => [
        {
            key: 'projects',
            name: 'HOP',
            minWidth: 220
        },
        {
            key: 'alertType',
            name: 'ALERT TYPE',
            minWidth: 220
        },
        {
            key: 'channel',
            name: 'CHANNELS',
            minWidth: 200
        },
        {
            key: 'recipient',
            name: 'RECIPIENT/URL',
            minWidth: 430,
            formatter: ({ row }: { row: AlertHopTableRow }) =>
                renderRecipient(row.recipient, row.channel)
        },
        {
            key: 'actions',
            name: 'ACTIONS',
            width: 110,
            formatter: ({ row }: { row: AlertHopTableRow }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <IconButton onClick={() => handleEditClick(row.rowIndex, 'hop')} size="small">
                        <EditIcon sx={{ color: colors.inputNew.inputIcon }} />
                    </IconButton>
                    <IconButton onClick={() => handleRemoveHopRow(row.rowIndex)} size="small">
                        <DeleteIcon sx={{ color: colors.inputNew.removeIcon }} />
                    </IconButton>
                </Box>
            )
        }
    ];

    return (
        <Box
            sx={{
                mt: 3,
                px: 0,
                py: 3,
                borderRadius: '8px',
                backgroundColor: colors.dataPipeline.accordionWidget.background.page
            }}
        >
            <AlertErrorDialog
                open={errorDialog.open}
                errorMessage={errorDialog.message}
                onClose={() => setErrorDialog({ open: false, message: '' })}
            />

            <Typography
                variant="body2"
                sx={{
                    opacity: 0.7,
                    mb: 3,
                    fontStyle: 'italic',
                    color: colors.textLight.main
                }}
            >
                ⓘ This section is to setup the alerts.
            </Typography>
            
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                }}
            >
                <Typography
                    variant="subHeader"
                    sx={{
                        color: colors.dataPipeline.accordionWidget.text.link,
                        whiteSpace: 'nowrap'
                    }}
                >
                    Enable Alerts
                </Typography>

                <Box
                    sx={{
                        flexGrow: 1,
                        height: '1px',
                        ml: 1,
                        backgroundColor: colors.dataPipeline.accordionWidget.border.expandedAccordion
                    }}
                />
            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {showFlowSection && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <AntSwitch
                                    checked={flowAlertsEnabled}
                                    onChange={(event) => setFlowAlertsEnabled(event.target.checked)}
                                    sx={toggleSx}
                                />

                                <Typography
                                    variant="subHeader"
                                    sx={{
                                        color: colors.toggle.text
                                    }}
                                >
                                    Configure Flow/Program Level Alerts.
                                </Typography>
                            </Box>

                            {flowAlertsEnabled && (
                                <>
                                    <CustomizedDataGrid
                                        tableColumns={getFlowColumns()}
                                        tableData={flowTableData}
                                        tableType="flow-alert-configuration"
                                        defaultColumnNames={FLOW_DEFAULT_COLUMNS}
                                        listLength={flowTableData.length}
                                        isFetching={false}
                                        isRenderPagination={false}
                                        isShowMoreButton={false}
                                        autoSetDraggedColumns
                                        isLighterTheme
                                        isDateRangeFilter={false}
                                        isDisabled={false}
                                        showExport={false}
                                    />

                                    <Typography
                                        variant="labelText"
                                        onClick={() => handleAddRowClick('flow')}
                                        sx={{
                                            color: colors.inputNew.addText,
                                            cursor: 'pointer',
                                            mt: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: 'fit-content'
                                        }}
                                    >
                                        {showAddForm === 'flow' && !editingIndex
                                            ? <RemoveIcon sx={{ mr: 1 }} />
                                            : <AddIcon sx={{ mr: 1 }} />
                                        }
                                        Add Row
                                    </Typography>

                                    <Collapse
                                        in={showAddForm === 'flow'}
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        {renderAddAlertForm()}
                                    </Collapse>
                                </>
                            )}
                        </>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 2,
                            mb: 1
                        }}
                    >
                        <AntSwitch
                            checked={hopAlertsEnabled}
                            onChange={(event) => setHopAlertsEnabled(event.target.checked)}
                            sx={toggleSx}
                        />

                        <Typography
                            variant="subHeader"
                            sx={{
                                color: colors.toggle.text
                            }}
                        >
                            Configure Hop Level Alerts.
                        </Typography>
                    </Box>

                    {hopAlertsEnabled && (
                        <>
                            <CustomizedDataGrid
                                tableColumns={getHopColumns()}
                                tableData={hopTableData}
                                tableType="hop-alert-configuration"
                                defaultColumnNames={HOP_DEFAULT_COLUMNS}
                                listLength={hopTableData.length}
                                isFetching={false}
                                isRenderPagination={false}
                                isShowMoreButton={false}
                                autoSetDraggedColumns
                                isLighterTheme
                                isDateRangeFilter={false}
                                isDisabled={false}
                                showExport={false}
                            />

                            <Typography
                                variant="labelText"
                                onClick={() => handleAddRowClick('hop')}
                                sx={{
                                    color: colors.inputNew.addText,
                                    cursor: 'pointer',
                                    mt: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: 'fit-content'
                                }}
                            >
                                {showAddForm === 'hop' && !editingIndex
                                    ? <RemoveIcon sx={{ mr: 1 }} />
                                    : <AddIcon sx={{ mr: 1 }} />
                                }
                                Add Row
                            </Typography>

                            <Collapse
                                in={showAddForm === 'hop'}
                                timeout="auto"
                                unmountOnExit
                            >
                                {renderAddAlertForm()}
                            </Collapse>
                        </>
                    )}
                </>
            )}
        </Box>
    );
};


// ====================================================================================
// START: Main ExecutiveTemplate Component
// ====================================================================================

const ExecutiveTemplate = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    let programId = searchParams.get('programId');
    let programName = searchParams.get('programName');
    const isEdited = searchParams.get('isEdited') === 'true';
    const sectorLabel = searchParams.get('sector');
    const subHeader = isEdited ? `Edit ${programName}` : 'Executive Template Setup';

    const getExecutiveBoardRoute = () => {
        if (!sectorLabel) {
            return 'dataOps';
        }

        switch (sectorLabel?.toLowerCase()) {
            case 'apac':
                return 'executiveOps/apac';
            case 'flna':
                return 'executiveOps/flna';
            case 'pfna':
                return 'executiveOps/pfna';
            case 'amesa':
                return 'executiveOps/emea';
            case 'pbna':
            default:
                return 'executiveOps';
        }
    };

    const headerLink = getExecutiveBoardRoute();
    const getBoardRedirectPath = () => `/data-pipeline/${headerLink}`;

    // *** THIS IS THE FIX ***
    // Re-added the useNavigate hook
    const navigate = useNavigate();
    // ***********************
    
    // Helper function to clear session storage
    const clearSessionData = () => {
        sessionStorage.removeItem('executiveTemplateTab');
        sessionStorage.removeItem('executiveTemplateDashboard');
        sessionStorage.removeItem('executiveTemplateWorkflow');
        sessionStorage.removeItem('executiveTemplateHops');
        sessionStorage.removeItem('executiveTemplateSla'); // Also clear SLA data
    };
    
    // Load activeTab from session storage or URL params
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = sessionStorage.getItem('executiveTemplateTab');
        if (isEdited) {
            return 0;
        }
        if (tabParam) {
            return Number.parseInt(tabParam, 10) || 0;
        }
        if (savedTab) {
            return Number.parseInt(savedTab, 10) || 0;
        }
        return 0;
    });

    const [isDashboardValid, setIsDashboardValid] = useState(false);
    const [isWorkflowValid, setIsWorkflowValid] = useState(false);
    const [isSlaValid, setIsSlaValid] = useState<boolean>(false);

    // Track alert IDs created in CREATE mode for activation after program submission
    const [createdAlertIds, setCreatedAlertIds] = useState<string[]>([]);

    // Load slaData from session storage
    const [slaData, setSlaData] = useState<any>(() => {
        const savedData = sessionStorage.getItem('executiveTemplateSla');
        return savedData ? JSON.parse(savedData) : {};
    });
    
    const [openDialogSuccess, setOpenDialogSuccess] = useState(false)

    // Load formDataDashboard from session storage
    const [formDataDashboard, setFormDataDashboard] = useState<any>(() => {
        const savedData = sessionStorage.getItem(
            'executiveTemplateDashboard'
        );

        const defaultData = {
            dashboardName: '',

            business: '',

            sector: '',

            frequency: '',

            gdo: '',

            market: '',

            businessStream: '',

            domain: '',

            businessApplication: '',

            dashboardType: '',

            businessDomain: 'Business Domain',

            businessProgram: '',

            description: 'Description',

            consumer: 'Primary Consumer/End Consumer',

            dependentReports: 'Dependent Reports',

            dataClassification: 'Data Classification',

            downstreamSys: 'Downstream Systems',

            dpmEmail: null,

            dpmContact: '',

            sustainleadEmail: null,

            sustainleadContact: '',

            escalationpointEmail: 'Escalation Point',

            escalationpointContact: '3243242342'
        };

        if (savedData) {
            const parsedData = JSON.parse(savedData);

            return {
                ...defaultData,
                ...parsedData
            };
        }

        return defaultData;
    });

    const initialHopData = {
        hopName: '', 
        sourceSystem: 'Source Name', 
        targetSystem: 'Target Name', 
        toolProcess: 'Tool Process', 
        program: '', project: '', 
        dependency: 'Dependency', 
        branchingLogic: 'Branching Logic', 
        downstreamReport: 'DownStream Report', 
        dataVolume: 'Data Volume', 
        dataFormat: 'Data Format', 
        escalationpointEmailWorkflow: null, 
        escalationpointContactWorkflow: '', 
        smeEmail: null, 
        smeContact: '',
        version: 'V2'
    };
    
    // Load hops from session storage
    const [hops, setHops] = useState<any[]>(() => {
        const savedData = sessionStorage.getItem('executiveTemplateHops');
        const defaultHops = [
            { ...initialHopData, hopName: 'Demo, Test' },
            { ...initialHopData, hopName: 'Pepwise' }
        ];
        return savedData ? JSON.parse(savedData) : defaultHops;
    });

    // Initialize detailsOpen based on hops length
    const [detailsOpen, setDetailsOpen] = useState<boolean[]>(() => {
        const savedData = sessionStorage.getItem('executiveTemplateHops');
        const defaultHops = [
            { ...initialHopData, hopName: 'Demo, Test' },
            { ...initialHopData, hopName: 'Pepwise' }
        ];
        const hopsArray = savedData ? JSON.parse(savedData) : defaultHops;
        return hopsArray.map(() => true); // Create [true, true, true, ...] based on hops count
    });

    // Load workflowData from session storage
    const [workflowData, setWorkflowData] = useState<any[]>(() => {
        const savedData = sessionStorage.getItem('executiveTemplateWorkflow');
        return savedData ? JSON.parse(savedData) : [];
    });
    // --- END: MODIFIED STATE HOOKS ---


    const [projectLists, setProjectLists] = useState<any[]>([[], []]);

    // --- START: ADDED useEffects to save state ---
    useEffect(() => {
        sessionStorage.setItem('executiveTemplateTab', activeTab.toString());
    }, [activeTab]);

    useEffect(() => {
        // Don't save empty/default dashboard data if a real one exists
        if (formDataDashboard.dashboardName) {
            sessionStorage.setItem('executiveTemplateDashboard', JSON.stringify(formDataDashboard));
        }
    }, [formDataDashboard]);

    useEffect(() => {
        if (workflowData.length > 0) {
            sessionStorage.setItem('executiveTemplateWorkflow', JSON.stringify(workflowData));
        }
    }, [workflowData]);

    useEffect(() => {
        sessionStorage.setItem('executiveTemplateHops', JSON.stringify(hops));
    }, [hops]);

    useEffect(() => {
        setDetailsOpen(prev => {
            if (prev.length < hops.length) {
                // Add new true values for new hops
                return [...prev, ...new Array(hops.length - prev.length).fill(true)];
            } else if (prev.length > hops.length) {
                // Remove values if hops were deleted
                return prev.slice(0, hops.length);
            }
            return prev;
        });
    }, [hops.length]);

    useEffect(() => {
        if (Object.keys(slaData).length > 0) {
            sessionStorage.setItem('executiveTemplateSla', JSON.stringify(slaData));
        }
    }, [slaData]);

    // Fetch dashboard data from API when in edit mode 
    useEffect(() => {
        const fetchDashboardData = async () => {
            if (isEdited && programId && programName) {
                try {
                    setLoading(true);
                    const res = await fetchExecutiveDashboardData(
                    {
                        executiveProgramId: programId,
                        sector: sectorLabel?.toUpperCase()
                    });
            
                    if (res?.success && res?.data) {
                        const data = res.data;

                        // Map API response to formDataDashboard state
                        const fetchedData = {
                            // Configuration
                            dashboardName:
                                data.dashboardDetails?.configuration?.dashboardName || '',

                            business:
                                data.dashboardDetails?.configuration?.business || '',

                            sector:
                                data.dashboardDetails?.configuration?.sectorName || '',

                            frequency:
                                data.dashboardDetails?.configuration?.frequency || '',

                            businessProgram:
                                data.dashboardDetails?.configuration?.businessProgram || '',

                            dashboardType:
                                data.dashboardDetails?.configuration?.dashboardType || '',

                            gdo:
                                data.dashboardDetails?.configuration?.gdo || '',

                            market:
                                data.dashboardDetails?.configuration?.market || '',

                            businessStream:
                                data.dashboardDetails?.configuration?.businessStream || '',

                            domain:
                                data.dashboardDetails?.configuration?.domain || '',

                            businessApplication:
                                data.dashboardDetails?.configuration?.businessApplication || '',

                            // Existing fields
                            businessDomain:
                                data.dashboardDetails?.configuration?.businessDomain || '',

                            description:
                                data.dashboardDetails?.configuration?.description || '',

                            consumer:
                                data.dashboardDetails?.configuration?.consumer || '',

                            dependentReports:
                                data.dashboardDetails?.configuration?.dependentReports || '',

                            dataClassification:
                                data.dashboardDetails?.configuration?.dataClassification || '',

                            downstreamSys:
                                data.dashboardDetails?.configuration?.downstreamSys || '',

                            // DPM
                            dpmUserContactId:
                                data.dashboardDetails?.contacts?.dpmUserContactId || null,

                            dpmEmail:
                                data.dashboardDetails?.contacts?.dpmUserDetail || null,

                            dpmContact:
                                data.dashboardDetails?.contacts?.dpmContactNum || '',

                            // Sustain Lead
                            sustainLeadContactId:
                                data.dashboardDetails?.contacts?.sustainLeadContactId || null,

                            sustainleadEmail:
                                data.dashboardDetails?.contacts?.sustainLeadUserDetail || null,

                            sustainleadContact:
                                data.dashboardDetails?.contacts?.sustainLeadContactNum || '',

                            // Escalation Point
                            escalationpointEmail:
                                data.dashboardDetails?.contacts?.escalationPointUserDetail || '',

                            escalationpointContact:
                                data.dashboardDetails?.contacts?.escalationPointContact || '',
                        };
                        
                        setFormDataDashboard(fetchedData);
                        sessionStorage.setItem('executiveTemplateDashboard', JSON.stringify(fetchedData));
                        
                        // WorkflowConfiguration
                        const wf = data.workflowConfiguration || [];
                        const mappedWorkflow = wf.map((item: any) => ({
                            hopName: item.hopDetails?.hopName || '',
                            projectId: item.hopDetails?.projectId || item.hopDetails?.project_id || '',
                            program: {
                                Program_id: item.hopDetails?.programId || item.hopDetails?.program_id || '',
                                Program_Name: item.hopDetails?.programName || item.hopDetails?.programFriendlyName || ''
                            },
                            project: {
                                Project_Id: item.hopDetails?.executiveProjectId || item.hopDetails?.executive_project_id || '',
                                Project_Name: item.hopDetails?.projectName || item.hopDetails?.projectFriendlyName || ''
                            },
                            version: item.hopDetails?.version.toUpperCase() || 'V2',
                            sourceSystem: item.hopDetails?.sourceSystem,
                            targetSystem: item.hopDetails?.targetSystem,
                            toolProcess: item.hopDetails?.toolProcess,
                            branchingLogic: item.hopDetails?.branchingLogic,
                            downstreamReport: item.hopDetails?.downstreamReport,
                            dataVolume: item.hopDetails?.dataVolume,
                            dataFormat: item.hopDetails?.dataFormat,
                            dependency: item.hopDetails?.dependency,
                            escalationPointUserContactId: item.contacts?.escalationPointUserContactId || null,
                            escalationpointEmailWorkflow: item.contacts?.escalationPointUserDetail || null,
                            escalationpointContactWorkflow: item.contacts?.escalationPointContact || '',
                            smeUserContactId: item.contacts?.smeUserContactId || null,
                            smeEmail: item.contacts?.SMEUserDetail || null,
                            smeContact: item.contacts?.SMEContact || ''
                        }));

                        setWorkflowData(mappedWorkflow);
                        sessionStorage.setItem('executiveTemplateWorkflow', JSON.stringify(mappedWorkflow));

                        // derive UI hops (use sensible defaults for fields not returned by API)
                        const mappedHops = mappedWorkflow.map((w: any) => ({
                            hopName: w.hopName || '',
                            projectId: w.projectId || '',
                            sourceSystem: w.sourceSystem || 'Source Name',
                            targetSystem: w.targetSystem || 'Target Name',
                            toolProcess: w.toolProcess || 'Tool Process',
                            program: w.program || {},
                            project: w.project || {},
                            dependency: w.dependency || 'Dependency',
                            branchingLogic: w.branchingLogic || 'Branching Logic',
                            downstreamReport: w.downstreamReport || 'DownStream Report',
                            dataVolume: w.dataVolume || 'Data Volume',
                            dataFormat: w.dataFormat || 'Data Format',
                            escalationPointUserContactId: w.escalationPointUserContactId || null,
                            escalationpointEmailWorkflow: w.escalationpointEmailWorkflow || null,
                            escalationpointContactWorkflow: w.escalationpointContactWorkflow || '',
                            smeUserContactId: w.smeUserContactId || null,
                            smeEmail: w.smeEmail || null,
                            smeContact: w.smeContact || '',
                            version: w.version || 'V2'
                        }));

                        setHops(mappedHops);
                        sessionStorage.setItem('executiveTemplateHops', JSON.stringify(mappedHops));
                    }
                    setLoading(false);
                } catch (error) {
                    console.error('Error fetching dashboard data:', error);
                    setLoading(false);
                }
            }
        };
        
        fetchDashboardData();
    }, [isEdited, programId, programName]);
    // --- END: ADDED useEffects to save state ---


    // --- START: MODIFIED HANDLERS to clear session data ---
    const handleCancel = async () => { 
        clearSessionData();
        navigate(getBoardRedirectPath()); 
    };
    
    const buildExecutiveDashboardPayload = () => {
        const dashboardDetails = {
            configuration: {
                dashboardName: formDataDashboard.dashboardName || "",

                business: formDataDashboard.business || "",

                sectorName: formDataDashboard.sector || "",

                frequency: formDataDashboard.frequency || "",

                businessProgram: formDataDashboard.businessProgram || "",

                gdo: formDataDashboard.gdo || "",

                market: formDataDashboard.market || "",

                businessStream:
                    formDataDashboard.businessStream || "",

                domain:
                    formDataDashboard.domain || "",

                businessApplication:
                    formDataDashboard.businessApplication || "",

                dashboardType:
                    formDataDashboard.dashboardType || ""
            },

            contacts: {
                dpmUserContactId:
                    formDataDashboard.dpmUserContactId || "",

                dpmUserDetail:
                    formDataDashboard.dpmEmail || "",

                dpmContactNum:
                    formDataDashboard.dpmContact || "",

                sustainLeadContactId:
                    formDataDashboard.sustainLeadContactId || "",

                sustainLeadUserDetail:
                    formDataDashboard.sustainleadEmail || "",

                sustainLeadContactNum:
                    formDataDashboard.sustainleadContact || "",

                escalationPointUserDetail:
                    formDataDashboard.escalationpointEmail || "",

                escalationPointContact:
                    formDataDashboard.escalationpointContact || ""
            }
        };

        let orderCounter = 0;

        const workflowConfiguration = (workflowData || []).map(
            (hop: any) => {

                const isDeleted = hop.isDeleted ? true : false;

                const order = !isDeleted
                    ? orderCounter++
                    : null;

                return {
                    hopDetails: {
                        hopName: hop.hopName || "",

                        projectId:
                            hop.projectId || "",

                        programId:
                            hop.program?.Program_id || "",

                        programName:
                            hop.program?.Program_Name || "",

                        programFriendlyName:
                            hop.program?.Program_Friendly_Name ||
                            hop.program?.Program_Name ||
                            "",

                        executiveProjectId:
                            hop.project?.Project_Id || "",

                        projectName:
                            hop.project?.Project_Name || "",

                        projectFriendlyName:
                            hop.project?.Project_Friendly_Name || "",

                        version:
                            hop.version?.toLowerCase() || "v2",

                        isDeleted,

                        order
                    },

                    contacts: {
                        escalationPointUserContactId:
                            hop.escalationPointUserContactId || "",

                        escalationPointUserDetail:
                            hop.escalationpointEmailWorkflow || "",

                        escalationPointContact:
                            hop.escalationpointContactWorkflow || "",

                        smeUserContactId:
                            hop.smeUserContactId || "",

                        SMEUserDetail:
                            hop.smeEmail || "",

                        SMEContact:
                            hop.smeContact || ""
                    }
                };
            }
        );

        return {
            dashboardDetails,
            workflowConfiguration,
            slaConfiguration: slaData,
            isEdit: isEdited,
            executiveProgramId: programId || ""
        };
    };

    const shouldMoveToNextTab = (): boolean =>
        (!isEdited && activeTab < 3) || (isEdited && activeTab < 1);

    const shouldSubmitDashboard = (): boolean =>
        (!isEdited && activeTab === 3) || (isEdited && activeTab === 1);

    const submitExecutiveDashboard = async () => {
        const payload = buildExecutiveDashboardPayload();
        setLoading(true);
        try {
            let result: any; 
            if (!isEdited && activeTab === 3) result = await executiveProgramProjects(payload); // Use executiveProgramProjects -> Cosmos create API (active)
            if (isEdited && activeTab === 1) result = await executiveProgramProjectsUpdate(payload); 

            if (result.statusCode === 201 || (isEdited && result.statusCode === 200)) {
                // Activate all alerts created in CREATE mode
                if (result.statusCode === 201 && createdAlertIds.length > 0) {
                    console.log(`Activating ${createdAlertIds.length} alerts after program creation`);

                    const failedAlerts: string[] = [];
                    const activationPromises = createdAlertIds.map(async (alertId) => {
                        try {
                            await activateAlertConfig(alertId);
                            console.log(`Alert ${alertId} activated successfully`);
                        } catch (activationError) {
                            console.error(`Failed to activate alert ${alertId}:`, activationError);
                            failedAlerts.push(alertId);
                        }
                    });

                    await Promise.all(activationPromises);
                    setCreatedAlertIds([]);

                    // Log activation results
                    const successCount = createdAlertIds.length - failedAlerts.length;
                    if (failedAlerts.length > 0) {
                        console.warn(`Alert activation: ${successCount} succeeded, ${failedAlerts.length} failed. Failed IDs:`, failedAlerts);
                    } else {
                        console.log(`All ${successCount} alerts activated successfully`);
                    }
                }

                setOpenDialogSuccess(true);
                clearSessionData();
            }
        } catch (error) {
            console.error("Submission failed", error);
        } finally {
            setLoading(false);
        }
    };

    // Callback for tracking alerts created in CREATE mode
    // Note: Only called when programId is falsy (CREATE mode)
    const handleAlertCreated = (alertId: string) => {
        console.log(`Tracking alert for later activation: ${alertId}`);
        setCreatedAlertIds(prev => [...prev, alertId]);
    };

    const handleSaveNext = async () => {
        if (shouldMoveToNextTab()) {
            setActiveTab(activeTab + 1);
            return;
        }

        if (shouldSubmitDashboard()) {
            await submitExecutiveDashboard();
        }
    };


    const handleTabChange = async (_: React.SyntheticEvent, newValue: number) => { 
        if (newValue <= activeTab) {
            setActiveTab(newValue);
            return;
        }
    
        // This logic seems to just step one-by-one, let's keep it
        // but the main fix is that state is saved, so even if this logic
        // is just for validation, the data won't be lost.
        setLoading(true);
        try {
            // This 'handleSaveNext' will just increment the tab,
            // which is fine. We'll then set the tab to the real destination.
            if(!isEdited) await handleSaveNext(); 
            setActiveTab(newValue); // Set the tab to where the user actually clicked
        } catch (error) {
            console.error("Failed to save data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessClose = () => { 
        clearSessionData(); // Also clear here
        setOpenDialogSuccess(false);
        // Use navigate, not navigator
        navigate(`/data-pipeline/home/executiveOps`); 
    };
    const handleSuccessYes = () => { 
        clearSessionData(); // And here
        setOpenDialogSuccess(false);
        // Use navigate, not navigator
        navigate(getBoardRedirectPath());
    };
    // --- END: MODIFIED HANDLERS ---

    const isSaveNextDisabled = (): boolean => {
        switch (activeTab) {
            case 0: return !isDashboardValid;
            case 1: return !isWorkflowValid;
            case 2: return !isSlaValid;
            case 3: return false;
            default: return true;
        }
    };

    // Get the Program ID from the 'workflowData' state, which is set in Tab 2.
    // This will now be populated from session storage on refresh.
    const programIdFromWorkflow = workflowData[0]?.program?.Program_id;

    return loading ? (
        <Loader />
    ) : (
        <PageWrapper>
            <PageHeader title={[{ text: "Programs", link: `/data-pipeline/${headerLink}` }, { text: subHeader }]} icon={DashboardIcon}>
                <Typography variant='labelText' style={{ color: `${colors.textLight.secondary}`, cursor: 'pointer', marginTop: '6px', marginRight: '25px' }} onClick={handleCancel}>CANCEL</Typography>
                <Button variant="pepwiseSecondary" onClick={handleSaveNext} disabled={isSaveNextDisabled()}>
                    {activeTab === 3 ? 'SAVE & SUBMIT FOR APPROVAL' : 'SAVE & NEXT'}
                </Button>
                <ExecutiveTemplateSuccessConfirmationDialog open={openDialogSuccess} onConfirmClose={handleSuccessClose} onYes={handleSuccessYes} />
            </PageHeader>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="inherit"
                variant="fullWidth"
                TabIndicatorProps={{
                    style: {
                    display: 'none'
                    }
                }}
                sx={{
                    px: 4,
                    mt: 2,

                    '& .MuiTabs-indicator': {
                    display: 'none'
                    },

                    '& .MuiTab-root': {
                    ...theme.typography.tabLabel,
                    color: `${theme.palette.Tab.inActive.newColor} !important`,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    minHeight: '50px',
                    padding: '8px 16px',
                    width: '41vh !important',
                    backgroundColor: `${theme.palette.Tab.inActive.bgColor} !important`,
                    borderBottom: 'none !important',
                    opacity: 1
                    },

                    '& .MuiTab-root.Mui-selected, & .MuiTab-root.Mui-selected:hover': {
                    color: `${theme.palette.Tab.Active.newColor} !important`,
                    backgroundColor: `${theme.palette.Tab.Active.newBgColor} !important`,
                    borderBottom: `4px solid ${theme.palette.Tab.Active.newBorderColor} !important`
                    },

                    '& .MuiTab-root.Mui-disabled': {
                    color: `${theme.palette.Tab.inActive.color} !important`,
                    backgroundColor: `${theme.palette.Tab.inActive.bgColor} !important`,
                    opacity: 0.5
                    }
                }}
                >
                <Tab label="Dashboard Details" />
                <Tab label="Workflow Configuration" />
                <Tab label="SLAs" />
                <Tab label="Alerts" />
                </Tabs>

            <Box sx={{ px: 4 }}>
                {activeTab === 0 && <DashboardDetails formDataDashboard={formDataDashboard} setFormDataDashboard={setFormDataDashboard} onValidityChange={setIsDashboardValid} />}
                {activeTab === 1 && <WorkflowConfiguration hops={hops} setHops={setHops} detailsOpen={detailsOpen} setDetailsOpen={setDetailsOpen} projectLists={projectLists} setProjectLists={setProjectLists} onValidityChange={setIsWorkflowValid} onDataChange={setWorkflowData} />}
                {activeTab === 2 && <SLAs onValidityChange={setIsSlaValid} onDataChange={setSlaData} />}
                
                {/* This will now receive the re-hydrated props and fetch alerts correctly */}
                {activeTab === 3 && <Alerts formDataDashboard={formDataDashboard} hops={hops} programId={programIdFromWorkflow} onAlertCreated={handleAlertCreated} />}
            </Box>
        </PageWrapper>
    );
};

export default ExecutiveTemplate;